import { useEffect, useRef, useState } from 'react'
import { Briefcase, Users2, Sparkles, type LucideIcon } from 'lucide-react'
import SectionHeading from './SectionHeading'
import ExperienceTimeline from './ExperienceTimeline'
import LeadershipTimeline from './LeadershipTimeline'
import SkillsGrid from './SkillsGrid'
import { initGSAP, prefersReducedMotion } from '../lib/motion'

interface TabDef {
  id: string
  label: string
  Icon: LucideIcon
  Content: React.FC
}

const TABS: TabDef[] = [
  { id: 'experience', label: 'Experience', Icon: Briefcase, Content: ExperienceTimeline },
  { id: 'leadership', label: 'Leadership', Icon: Users2, Content: LeadershipTimeline },
  { id: 'skills', label: 'Skills', Icon: Sparkles, Content: SkillsGrid },
]

const Experience: React.FC = () => {
  const ref = useRef<HTMLDivElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const indicatorRef = useRef<HTMLSpanElement>(null)
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([])
  const prevActiveRef = useRef<TabDef['id']>(TABS[0].id)
  const [activeId, setActiveId] = useState<TabDef['id']>(TABS[0].id)

  const activeIndex = TABS.findIndex((tab) => tab.id === activeId)
  const ActiveContent = TABS[activeIndex].Content

  const positionIndicator = (index: number, animate: boolean) => {
    const btn = tabRefs.current[index]
    const indicator = indicatorRef.current
    if (!btn || !indicator) return
    const gsapApi = initGSAP()
    if (!animate || prefersReducedMotion()) {
      gsapApi.set(indicator, { x: btn.offsetLeft, width: btn.offsetWidth })
      return
    }
    gsapApi.to(indicator, {
      x: btn.offsetLeft,
      width: btn.offsetWidth,
      duration: 0.45,
      ease: 'power3.out',
      overwrite: 'auto',
    })
  }

  const activateTab = (index: number) => {
    const next = (index + TABS.length) % TABS.length
    setActiveId(TABS[next].id)
    tabRefs.current[next]?.focus()
  }

  const handleKeyDown = (event: React.KeyboardEvent) => {
    const last = TABS.length - 1
    let next: number | undefined
    if (event.key === 'ArrowRight') next = (activeIndex + 1) % TABS.length
    else if (event.key === 'ArrowLeft') next = (activeIndex - 1 + TABS.length) % TABS.length
    else if (event.key === 'Home') next = 0
    else if (event.key === 'End') next = last
    if (next === undefined) return
    event.preventDefault()
    activateTab(next)
  }

  useEffect(() => {
    if (prefersReducedMotion()) return
    const gsapApi = initGSAP()
    const ctx = gsapApi.context(() => {
      gsapApi.fromTo(
        '[data-tabs-reveal]',
        { opacity: 0, y: 24 },
        {
          opacity: 1,
          y: 0,
          duration: 0.6,
          ease: 'power2.out',
          scrollTrigger: { trigger: ref.current, start: 'top 78%', once: true },
        }
      )
    }, ref)
    return () => ctx.revert()
  }, [])

  useEffect(() => {
    positionIndicator(activeIndex, false)
    const onResize = () => positionIndicator(activeIndex, false)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (prevActiveRef.current === activeId) return
    prevActiveRef.current = activeId
    positionIndicator(activeIndex, true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId])

  useEffect(() => {
    if (prefersReducedMotion()) return
    const panel = panelRef.current
    if (!panel) return
    const gsapApi = initGSAP()
    const ctx = gsapApi.context(() => {
      gsapApi.fromTo(
        '[data-tab-panel]',
        { opacity: 0, y: 16 },
        { opacity: 1, y: 0, duration: 0.45, ease: 'power3.out' }
      )
    }, panel)
    return () => ctx.revert()
  }, [activeId])

  return (
    <section
      id="experience"
      ref={ref}
      className="section-padding bg-neutral-50 dark:bg-neutral-900/30"
    >
      <div className="container-width">
        <SectionHeading
          label="Experience"
          meta="career · community · tools"
          title={
            <>
              Where I've <span className="text-gradient">built things</span>
            </>
          }
          description="From internships to production platform engineering, alongside community leadership and the tooling I operate every day."
        />

        <div data-tabs-reveal>
          <div
            role="tablist"
            aria-label="Career sections"
            onKeyDown={handleKeyDown}
            className="relative inline-flex items-center gap-1 border-b border-neutral-200 dark:border-white/10"
          >
            {TABS.map((tab, i) => {
              const selected = tab.id === activeId
              return (
                <button
                  key={tab.id}
                  ref={(el) => {
                    tabRefs.current[i] = el
                  }}
                  id={`tab-${tab.id}`}
                  role="tab"
                  type="button"
                  aria-selected={selected}
                  aria-controls={`panel-${tab.id}`}
                  tabIndex={selected ? 0 : -1}
                  onClick={() => activateTab(i)}
                  className={`relative inline-flex items-center gap-2 px-3 py-4 font-mono text-xs uppercase tracking-[0.14em] transition-colors duration-200 sm:px-5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/60 focus-visible:ring-inset ${
                    selected
                      ? 'text-primary-600 dark:text-primary-400'
                      : 'text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100'
                  }`}
                >
                  <tab.Icon size={14} />
                  {tab.label}
                </button>
              )
            })}
            <span
              ref={indicatorRef}
              aria-hidden="true"
              className="absolute -bottom-px left-0 h-[2px] rounded-full bg-primary-500"
            />
          </div>
        </div>

        <div ref={panelRef} className="mt-10">
          <div
            data-tab-panel
            key={activeId}
            id={`panel-${activeId}`}
            role="tabpanel"
            aria-labelledby={`tab-${activeId}`}
            tabIndex={0}
            className="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/30 focus-visible:rounded-lg"
          >
            <ActiveContent />
          </div>
        </div>
      </div>
    </section>
  )
}

export default Experience